import theme from "../theme";
import {makeStyles} from "@material-ui/styles";
import { Button, Typography } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import React, {useContext, useEffect, useState} from "react";
import { getCartItemPrices } from 'functions/parseSWPrices';
import {TrackingContext} from "../providers/tracking";
import ProductThumbnail from "../products/thumbnail";
import Plus from "public/images/icons/plus.svg";
import Minus from "public/images/icons/minus.svg";
import Link from "next/link";
import StockIndicator from '@components/products/partials/StockIndicator';
import { renderPromotion } from '@functions/swCartLibrary';
import useCartStore from '@services/stores/CartStore';
import useSessionStore from '@services/stores/SessionStore';
import { WishlistCartItem } from '@components/checkout/WishlistCartItem';
import { getProductAnalytics } from '@tools/productAnalytics';
import { useRouter } from 'next/router';
import BrandName from '@components/products/partials/BrandName';
import useApplicationStore from '@services/stores/ApplicationStore';

const useStyles = makeStyles({
	item: {
		position: 'relative',
		display: 'flex',
		padding: '20px 0 25px',
		gap: '1rem',
		alignItems: 'center',
		'&:not(:first-of-type)': {
			borderBottom: '1px solid #f3f3f5',
		},
		'&.locked': {
			pointerEvents: 'none'
		},
		[theme.breakpoints.down('sm')]: {
			//gap: '10px',
		},
	},
	item__image: {
		position: 'relative',
		flexShrink: 0,
		width: 120,
		height: 'fit-content',
		'.compact &': {
			width: 80,
		},
		[theme.breakpoints.down('sm')]: {
			width: 90,
		},
		'&:after': {
			content: '""',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			position: 'absolute',
			//boxShadow: 'inset 0 0 90px 8px #f3f3f5',
			mixBlendMode: 'multiply',
			pointerEvents: 'none'
		}
	},
	item__info: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minWidth: 0,
		color: '#222',
		width: 'auto',
		'& a': {
			color: '#222',
		},

	},
	item__name: {
		fontSize: 15,
		fontWeight: 400,
		lineHeight: 1.2,
		marginBottom: '0',
		color: 'inherit',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		width: '90%'
	},
	item__variant: {
		display: 'inline-block',
		fontSize: 15,
		fontWeight: 400,
		color: 'inherit',
	},
	item__priceQty: {
		display: 'flex',
		justifyContent: 'space-between',
		gap: '10px',
		marginTop: 'auto'
	},
	item__prices: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'flex-end',
		marginTop: 'auto',
		position: 'relative',
		flexDirection: 'column',
		gap: '5px',
		minWidth: '60px',

	},
	item__price: {
		fontSize: 15,
		fontWeight: 400,
		lineHeight: '1',
		color: '#222',
		'&.special': {
			color: '#e65f5f', //'#ca0d0d'
			fontWeight: 500,
		},
		'&.old': {
			textDecoration: 'line-through',
			color: '#666',
		},
		'.compact &': {
			//lineHeight: '32px',
		}
	},
	item__label: {
		position: 'absolute',
		top: '27px',
		right: 0
	},
	qtyWrapper: {
		display: 'flex',
		marginTop: '1em'
	},
	qtyButton: {
		width: 32,
		height: 32,
		minWidth: 'auto',
		padding: 10,
		textAlign: 'center',
		appearance: 'none',
		cursor: 'pointer',
		border: '1px solid #ddd',
		borderRadius: 0,
		backgroundColor: '#fff',
		'&.increment': {
			borderRadius: 0
		},
		'.compact &': {
			width: 32,
			height: 32
		},
	},
	qtyIcon: {
		fontSize: 20,
		height: 20,
		width: 20,
		fontWeight: 400,
		strokeWidth: 0.35,
	},
	qtyInput: {
		width: 32,
		height: 32,
		fontSize: 13,
		padding: '0.5rem 0',
		margin: 0,
		textAlign: 'center',
		fontFamily: theme.typography.fontFamily,
		appearance: 'textfield',
		border: 'none',
		outline: 'none',
		backgroundColor: '#fff',
		borderTop: '1px solid #ddd',
		borderBottom: '1px solid #ddd',
		borderRadius: 0,
		'&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
			appearance: 'none',
			margin: 0
		},
		'.compact &': {
			width: 32,
			height: 32,
			fontSize: 13
		},
	},
	item__removeButton: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		position: 'absolute',
		right: 0,
		top: 22,
		zIndex: 3,
		padding: '0px',
		background: '#fff',
		//border: '4px solid white',
		color: '#777',
		//height: 28,
		//width: 28,
		cursor: 'pointer',
		transition: 'ease background 0.2s',
		'& > svg': {
			fill: '#333',
			fontSize: 16,
		},
		'&:hover': {
			background: 'white',
		},
	},
}, {name: "CheckoutCartItem"});

let updateTimeout;

/**
 *
 * @param {LineItem} item
 * @param isLocked
 * @param suspend
 * @param resume
 * @param reload
 * @param fullWidth
 * @param compact
 * @param readOnly
 * @returns {Element}
 * @constructor
 */
export default function CheckoutCartItem({
     item,
     isLocked,
     suspend,
     resume,
     reload,
     fullWidth = false,
     compact = false,
     readOnly = false,
}) {
	const formatPrice = useSessionStore((state) => state.methods.formatPrice);
	const [quantity, setQuantity] = useState(item.quantity);
	const classes = useStyles();
	const tracking = useContext(TrackingContext);
	const {updateCart, removeCartItem} = useCartStore((state) => state.methods);
	const cart = useCartStore((state) => state.cart);
	const router = useRouter();

	let prices = getCartItemPrices(item);

	useEffect(() => {
		clearTimeout(updateTimeout);
		if (item.quantity !== quantity && quantity > 0) {
			suspend(item.id);
			updateTimeout = setTimeout(async () => {
				await updateCart({itemId: item.id, quantity: quantity});
				await reload();
				resume(item.id);
			}, 300);
		}
	}, [quantity]);

	useEffect(() => {
		setQuantity(item.quantity);
	}, [item]);

	async function removeFromCart(item) {
		suspend(item.id);
		await removeCartItem({itemId: item.id});
		await reload();
		resume();

		await removeFromCartTracking(item.quantity);
	}

	async function removeFromCartTracking(quantity=1) {
		item['analytics'] = await getProductAnalytics(item.referencedId)
		tracking.actions.removeFromCart({
			id: item.payload.productNumber,
			price: item.price.unitPrice,
			discount: item.price?.listPrice?.discount,
			list: {id: 'cart', name: 'cart_decrement', location: router.asPath},
			quantity: quantity,
			analytics: item.analytics,
		});
	}

	async function addToCartTracking() {
		item['analytics'] = await getProductAnalytics(item.referencedId)

		tracking.actions.addToCart({
            id: item.payload.productNumber,
            price: item.price.unitPrice,
            discount: item.price?.listPrice?.discount,
            list: {id: 'cart', name: 'cart_increment', location: router.asPath},
			quantity: 1,
            analytics: item.analytics,
        });
	}

	function hasOptions(item) {
		return item.payload.options.length > 0;
	}

	function getVariantOption(item) {
		return item.payload.options.map((option) => {
			return option.option;
		}).join(' - ');
	}

	function getUrl() {
		return item.payload.npEnriched ? '/' + item.payload.npEnriched.seoUrls.seoPathInfo : '/detail/' + item.referencedId;
	}

	/**
	 * Item Quantity functions
	 */
	function increment() {
		setQuantity(parseInt(quantity, 10) + 1);
		addToCartTracking(quantity);
	}

	function decrement() {
		if (quantity > 1) {
			setQuantity(parseInt(quantity, 10) - 1);
			removeFromCartTracking();
		} else {
			setQuantity(0);
			removeFromCart(item);
		}
	}

	function onChange(value) {
		setQuantity(parseInt(value, 10));
	}

	return (
		<li className={[classes.item, compact ? 'compact' : '', isLocked && isLocked !== item.id ? 'locked' : ''].join(' ')}>

			<div className={classes.item__image}>
				<Link href={getUrl()}>
					<a>
						<ProductThumbnail image={item.cover} size={120} index={0}/>
					</a>
				</Link>
			</div>

			<div className={[classes.item__info, fullWidth ? 'fullWidth' : ''].join(' ')}>
				<Link href={getUrl()}>
					<a style={{ textDecoration: 'none' }}>
						{
							item?.payload?.manufacturer?.name &&
							<BrandName name={item?.payload?.manufacturerId}></BrandName>
						}
						<Typography variant="body1" className={classes.item__name}>{item.label}</Typography>

						{hasOptions(item) ?
							<Typography variant="body1" className={classes.item__variant}>
								{getVariantOption(item)}
							</Typography> : ''
						}

						{ renderPromotion(item, cart, formatPrice) }
					<StockIndicator
						style={{ padding: '0.75rem 0.1rem', width: '100%' }}
						deliveryTime={item?.deliveryInformation?.deliveryTime}>
					</StockIndicator>
					</a>
				</Link>

				{
					!readOnly && <div className={classes.item__removeButton}>
						<Close className={classes.item__remove} onClick={removeFromCart.bind(this, item)} />
					</div>
				}

				<WishlistCartItem className={classes.item__label} productId={item.referencedId}></WishlistCartItem>
				<div className={classes.item__priceQty}>
					<div className={classes.qtyWrapper}>
						<Button onClick={decrement} className={[classes.qtyButton, 'decrement'].join(' ')} disabled={readOnly}><Minus
							className={classes.qtyIcon}/></Button>
						<input type="number" autoComplete="off" autofill="off" value={quantity} name={'item-input-' + item.payload.productNumber} onChange={(event) => onChange(event.target.value)}
							   className={classes.qtyInput} disabled={readOnly}/>
						<Button onClick={increment} className={[classes.qtyButton, 'increment'].join(' ')} disabled={readOnly}><Plus
							className={classes.qtyIcon}/></Button>
					</div>

					<div className={classes.item__prices}>
						{prices.isOnSale ?
							<>
								<Typography variant="body1"
											className={[classes.item__price, 'old'].join(' ')}>{formatPrice(prices.listPrice)}</Typography>
								<Typography variant="body1"
											className={[classes.item__price, 'special'].join(' ')}>{formatPrice(prices.price)}</Typography>
							</>
							:
							<Typography variant="body1"
										className={classes.item__price}>{formatPrice(prices.price)}</Typography>
						}
					</div>
				</div>
			</div>
		</li>
	);
}
